import Guard from 'arg-guard'

import Logger from '../logger'
import REQUEST_HEADERS from './request-headers'
import Service from './service'
import ServiceOperation from './service-operation'

import User from '../models/user'

/**
 * Gateway for accessing the profile service.
 *
 * @export ProfileService
 * @class ProfileService
 */
export default class ProfileService extends Service {

    /**
     * Creates an instance of ProfileService.
     * @param {?number} schemeId The scheme for the active context.
     * @memberof ProfileService
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/profile', 'User')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Deletes a user.
     * @param {string} bearerToken The bearer token used by the account.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ProfileService
     */
    async deleteUserAsync(bearerToken) {
        const operationState = new ServiceOperation('Delete User', true)
        const url = `with-context/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [DELETE]')

        const config = this._buildAxiosConfig()

        config.headers[REQUEST_HEADERS.BEARER_TOKEN] = `Bearer ${ bearerToken }`

        try {
            return this._processResponse(await this.axios.delete(url, config), operationState, 'deleteUserAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'deleteUserAsync')
        }
    }

    /**
     * Loads user info.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ProfileService
     */
    async loadUserInfoAsync() {
        const operationState = new ServiceOperation('Load User Info', true)
        Logger.log(this.baseUrl, 'Request [GET]')

        try {
            return this._processData(await this.axios.get('', this._buildAxiosConfig()), operationState, 'loadUserInfoAsync',
                (sourceData) => new User(sourceData))
        } catch (err) {
            return this._failOperation(err, operationState, 'loadUserInfoAsync')
        }
    }

    /**
     * Updates consents for the user.
     * @param {string} consentType The type of consent - either 'marketing' or 'evaluation'.
     * @param {boolean} allow Whether the consent is granted or not.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ProfileService
     */
    async updateConsentAsync(consentType, allow) {
        Guard.throwIf(consentType, 'consentType')
        Guard.throwIfNull(allow, 'allow')

        const operationState = new ServiceOperation('Update Consent', true)
        const url = `${ consentType }`
        const data = {
            allow,
            schemeId: this.schemeId,
        }
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [POST]')

        try {
            return this._processData(await this.axios.post(url, data, this._buildAxiosConfig()), operationState, 'updateConsentAsync',
                (sourceData) => new User(sourceData))
        } catch (err) {
            return this._failOperation(err, operationState, 'updateConsentAsync')
        }
    }
}
